import React from "react";
import {
  Box,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import searchIcon from "@assets/facilityNetwork/lf-search.svg";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import {
  LINKED_FACILITY_STATUS_CHIPS,
} from "./facilityNetworkChildrenUtils";
import { FN } from "./facilityNetworkTheme";

type Props = {
  search: string;
  onSearchChange: (value: string) => void;
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  typeOptions: string[];
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
};

/** Figma 9142:99117 / 9142:99118 — filter dropdowns beside search */
const filterSelectSx = {
  height: 40,
  minWidth: 132,
  bgcolor: "#fff",
  borderRadius: "8px",
  fontFamily: FN.fontFamily,
  fontSize: 14,
  fontWeight: 600,
  color: FN.text,
  boxShadow: "0px 2px 2px rgba(194,194,194,0.25)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: FN.figmaCardBorder,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: FN.figmaCardBorder,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: FN.primary,
    borderWidth: 1,
  },
  "& .MuiSelect-select": {
    py: 0,
    px: 3,
    display: "flex",
    alignItems: "center",
    height: 40,
    boxSizing: "border-box",
    fontFamily: FN.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    color: FN.text,
  },
  "& .MuiSelect-icon": {
    color: FN.text,
    right: 12,
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: "8px",
      border: `1px solid ${FN.figmaCardBorder}`,
      boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
      "& .MuiMenuItem-root": {
        fontFamily: FN.fontFamily,
        fontSize: 14,
        fontWeight: 500,
        minHeight: 36,
        color: FN.text,
      },
      "& .MuiMenuItem-root.Mui-selected": {
        bgcolor: FN.metricCardBg,
        color: FN.pressed,
      },
    },
  },
};

export default function FacilityNetworkChildrenToolbar({
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  typeOptions,
  statusFilter,
  onStatusFilterChange,
}: Props) {
  const typeLabel = typeFilter === "all" ? "All Types" : typeFilter;
  const statusLabel =
    LINKED_FACILITY_STATUS_CHIPS.find((chip) => chip.id === statusFilter)?.label ??
    "All Status";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1,
        width: "100%",
        minHeight: 40,
      }}
    >
      <TextField
        size="small"
        placeholder="Search by name, NPI, city or state..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FacilityNetworkFigmaIcon src={searchIcon} size={16} />
            </InputAdornment>
          ),
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: "8px",
            bgcolor: "#fff",
            fontFamily: FN.fontFamily,
            fontSize: 14,
            boxShadow: "0px 2px 2px rgba(194,194,194,0.25)",
            "& fieldset": { borderColor: FN.figmaCardBorder },
          },
          "& input::placeholder": {
            color: FN.muted,
            opacity: 1,
          },
        }}
      />

      <Select
        value={typeFilter}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        onChange={(e) => onTypeFilterChange(String(e.target.value))}
        renderValue={() => typeLabel}
        MenuProps={menuProps}
        sx={filterSelectSx}
        inputProps={{ "aria-label": "Filter by facility type" }}
      >
        <MenuItem value="all">All Types</MenuItem>
        {typeOptions.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </Select>

      <Select
        value={statusFilter}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        onChange={(e) => onStatusFilterChange(String(e.target.value))}
        renderValue={() => statusLabel}
        MenuProps={menuProps}
        sx={filterSelectSx}
        inputProps={{ "aria-label": "Filter by status" }}
      >
        {LINKED_FACILITY_STATUS_CHIPS.map((chip) => (
          <MenuItem key={chip.id} value={chip.id}>
            {chip.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
